import { useState } from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import { Trash2, KeyRound, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Two-step secure delete dialog for ledgers.
 *
 * Stage 1: ask for the company "رمز الحذف" (deletion code).
 *          The DELETE call is performed with the entered code; on success,
 *          we treat that as both code-validation AND soft-delete trigger
 *          AFTER user confirms in stage 2. So we keep the code in state and
 *          only call the API after the user confirms.
 * Stage 2: confirmation: "هل تريد حذف هذه البيانات؟" Yes / No.
 *
 * Props:
 *   open, onOpenChange  — controlled dialog state
 *   ledgerId            — id to soft-delete
 *   clientName          — shown for context
 *   onDeleted           — called after successful delete
 *   testIdPrefix        — optional, defaults to "ledger"
 */
export default function DeleteLedgerDialog({
    open,
    onOpenChange,
    ledgerId,
    clientName,
    onDeleted,
    testIdPrefix = "ledger",
    deleteUrl,
}) {
    const [stage, setStage] = useState("code"); // "code" | "confirm"
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const { t } = useLang();

    const reset = () => {
        setStage("code");
        setCode("");
        setError("");
        setBusy(false);
    };

    const handleOpenChange = (v) => {
        if (!v) reset();
        onOpenChange?.(v);
    };

    const goToConfirm = (e) => {
        e?.preventDefault?.();
        setError("");
        if (!code) {
            setError(t.enterCodePlease);
            return;
        }
        setStage("confirm");
    };

    const performDelete = async () => {
        setBusy(true);
        setError("");
        try {
            await api.request({
                url: deleteUrl || `/ledgers/${ledgerId}`,
                method: "DELETE",
                data: { code },
            });
            toast.success(t.deletedToast);
            onDeleted?.();
            onOpenChange?.(false);
            reset();
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || t.deleteFail;
            // server rejected the code: jump back to stage 1 with an error
            setStage("code");
            setError(msg);
            setBusy(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={handleOpenChange}>
            <AlertDialogContent dir={t.dir} data-testid={`${testIdPrefix}-delete-dialog`}>
                {stage === "code" ? (
                    <>
                        <AlertDialogHeader className="text-start">
                            <AlertDialogTitle className="flex items-center gap-2 text-neutral-900">
                                <KeyRound className="h-5 w-5 text-amber-700" />
                                {t.deleteEnterCode}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {t.deleteEnterCodeHint}
                                {clientName ? (
                                    <span className="mt-1 block text-neutral-700">
                                        {t.recordLabel} <span className="font-bold">{clientName}</span>
                                    </span>
                                ) : null}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <form onSubmit={goToConfirm} className="mt-2 space-y-3">
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.deletionCodeFieldLabel}</Label>
                                <Input
                                    type="password"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    autoFocus
                                    className="h-11"
                                    data-testid={`${testIdPrefix}-delete-code-input`}
                                    placeholder="••••••••"
                                />
                                {error ? (
                                    <div
                                        className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                                        data-testid={`${testIdPrefix}-delete-code-error`}
                                    >
                                        {error}
                                    </div>
                                ) : null}
                                <p className="mt-2 text-xs text-neutral-500">
                                    {t.defaultDeletionCodeNote}
                                </p>
                            </div>
                        </form>
                        <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                            <AlertDialogCancel data-testid={`${testIdPrefix}-delete-code-cancel`}>
                                {t.cancel}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={goToConfirm}
                                className="bg-neutral-900 text-white hover:bg-neutral-800"
                                data-testid={`${testIdPrefix}-delete-code-next`}
                            >
                                {t.nextBtn}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </>
                ) : (
                    <>
                        <AlertDialogHeader className="text-start">
                            <AlertDialogTitle className="flex items-center gap-2 text-red-800">
                                <AlertTriangle className="h-5 w-5" />
                                {t.confirmDeleteTitle}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {t.confirmDeleteHint}
                                {clientName ? (
                                    <span className="mt-1 block text-neutral-700">
                                        {t.recordLabel} <span className="font-bold">{clientName}</span>
                                    </span>
                                ) : null}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                            <AlertDialogCancel
                                disabled={busy}
                                data-testid={`${testIdPrefix}-delete-confirm-cancel`}
                            >
                                {t.no}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(e) => {
                                    e.preventDefault();
                                    performDelete();
                                }}
                                disabled={busy}
                                className="bg-red-600 text-white hover:bg-red-700"
                                data-testid={`${testIdPrefix}-delete-confirm-yes`}
                            >
                                {busy ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <Trash2 className="ms-1 h-4 w-4" />}
                                {t.yesDelete}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </>
                )}
            </AlertDialogContent>
        </AlertDialog>
    );
}
